/**
 * "Send to Client?" — the one step that emails a report (Jim 2026-09-04): the
 * recipients the send will reach, Change who gets it (JIM-1027-13), and the
 * confirm. Mounted by the finish flow's review screen and, since Jim's
 * accidental send on 10/30, by the Unsent Reports list too.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Mail, Send, Users } from 'lucide-react';
import { inspectionService } from '../../../services/inspectionService';
import { ReportRecipientsEditor } from './ReportRecipientsEditor';

export function SendConfirmSheet({
  inspectionId,
  siteId,
  organizationId,
  onConfirm,
  onEdit,
  editLabel = 'Edit',
}: {
  inspectionId: string;
  siteId?: string;
  organizationId?: string | null;
  onConfirm: () => void;
  /** JIM-1006-11 (Jim 2026-10-06): the way out of the sheet is Edit, back to the answers. */
  onEdit: () => void;
  /** The way-out button's words; the Unsent Reports list says "Not now". */
  editLabel?: string;
}) {
  // JIM-1027-13: the contact tool opens over the sheet; the list refetches
  // when it closes.
  const [editingRecipients, setEditingRecipients] = useState(false);
  const { data, isPending, isError } = useQuery({
    queryKey: ['report-recipients', inspectionId],
    queryFn: () => inspectionService.getReportRecipients(inspectionId),
    retry: 1,
    staleTime: 60_000,
  });
  const recipients = data?.recipients ?? [];
  const count = recipients.length;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-confirm-title"
      className="fixed inset-0 z-[210] flex items-end sm:items-center justify-center bg-black/60 px-4 pb-4 sm:pb-0"
    >
      <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-success-100 dark:bg-success-900/50 text-success-700 dark:text-success-300 flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5" />
          </div>
          <h2
            id="send-confirm-title"
            className="text-lg font-semibold text-slate-900 dark:text-slate-100"
          >
            Send to Client?
          </h2>
        </div>

        {isPending ? (
          <p className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-2 mb-5">
            <Loader2 className="w-4 h-4 animate-spin" />
            Checking who receives this report…
          </p>
        ) : isError ? (
          <p className="text-sm text-danger-700 dark:text-danger-300 mb-5">
            Couldn&rsquo;t load the recipient list. Sending will email whoever
            is set up to receive reports for this site.
          </p>
        ) : count === 0 ? (
          <p className="text-sm text-slate-600 dark:text-slate-400 mb-5">
            No client contacts are set up to receive reports for this site.
            Sending will finalize the report without emailing anyone.
          </p>
        ) : (
          <div className="mb-5">
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
              The report will be emailed to{' '}
              {count === 1 ? 'this contact' : `these ${count} contacts`}:
            </p>
            <ul className="space-y-1.5 max-h-48 overflow-y-auto">
              {recipients.map((r) => (
                <li
                  key={r.email}
                  className="text-sm text-slate-800 dark:text-slate-200 flex items-baseline gap-2"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-success-500 flex-shrink-0 translate-y-[-2px]" />
                  <span>
                    {r.name}{' '}
                    <span className="text-slate-500 dark:text-slate-400">
                      ({r.email})
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {data?.client_auto_sign && (
          <p
            className="text-sm text-success-700 dark:text-success-300 mb-5"
            data-testid="client-auto-sign-note"
          >
            The client&rsquo;s signature ({data.client_auto_sign.signer_name})
            is applied automatically — no signature email goes out.
          </p>
        )}

        <div className="flex flex-col gap-3">
          {siteId && (
            <button
              type="button"
              onClick={() => setEditingRecipients(true)}
              data-testid="change-recipients"
              className="w-full py-2.5 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center justify-center gap-2"
            >
              <Users className="w-4 h-4" />
              Change who gets it
            </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className="w-full py-3 rounded-xl font-semibold bg-gradient-to-r from-success-500 to-success-600 hover:from-success-600 hover:to-success-700 text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            {isPending
              ? 'Send'
              : isError
                ? 'Send anyway'
                : count === 0
                  ? 'Finalize without emailing'
                  : `Send to ${count} ${count === 1 ? 'contact' : 'contacts'}`}
          </button>
          <button
            type="button"
            onClick={onEdit}
            className="w-full py-3 rounded-xl border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
          >
            {editLabel}
          </button>
        </div>
      </div>
      {editingRecipients && siteId && (
        <ReportRecipientsEditor
          siteId={siteId}
          organizationId={organizationId}
          inspectionId={inspectionId}
          onClose={() => setEditingRecipients(false)}
        />
      )}
    </div>
  );
}
